import type { Employee, Schedule, Shift, TimeSlots } from "@/types";

export type TooltipState = {
    x: number;
    y: number;
    lines: string[];
    shiftId: string;
    isPinned: boolean;
    timeRange: string;
    employeeName: string;
    roleName: string;
};

export const escapeHtml = (str: string): string =>
    String(str)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

export const extractRoleNames = (employee: any): string[] => {
    if (Array.isArray(employee?.roles)) {
        return employee.roles.map((r: any) => (typeof r === "string" ? r : r?.name)).filter(Boolean);
    }

    if (Array.isArray(employee?.skills)) {
        return employee.skills.filter(Boolean);
    }

    return [];
};

export const formatShortName = (name: string): string => {
    const parts = name.split(" ").filter(part => part.trim().length > 0);

    if (parts.length === 0) {
        return "";
    }

    if (parts.length === 1) {
        return parts[0]!;
    }

    const firstName = parts[0]!;
    const lastName = parts.slice(1).join(" ");

    return `${firstName.charAt(0).toUpperCase()}${lastName}`;
};

// Employee.name is stored as "First Last"; firstName/lastName are not always populated upstream.
export const getLastNameSortKey = (employee: Pick<Employee, "name" | "firstName" | "lastName">): string => {
    const parts = (employee.name || "").split(" ").filter(part => part.trim().length > 0);
    const lastName = employee.lastName?.trim() || parts.slice(1).join(" ");
    const firstName = employee.firstName?.trim() || parts[0] || "";

    return lastName ? `${lastName} ${firstName}` : firstName;
};

export const AVAILABILITY_CONFIG = {
    unavailable: { label: "Absent", bg: "#ef2929" },
    undesired: { label: "Indispo", bg: "#f57900" },
    desired: { label: "Désiré", bg: "#73d216" },
} as const;

const UNAVAILABLE_COLOR = AVAILABILITY_CONFIG.unavailable.bg;
const UNDESIRED_COLOR = AVAILABILITY_CONFIG.undesired.bg;
const DESIRED_COLOR = AVAILABILITY_CONFIG.desired.bg;
export const DEFAULT_COLOR = "#729fcf";
const UNQUALIFIED_COLOR = "#75507b";
const BRIS_DE_SERVICE = "#fc0505";
const BESOIN_A_COMBLER = "rgba(255,0,0,0.3)";
const OFFICIAL_SHIFT_COLOR = "#aea6ff";
const PINNED_SHIFT_COLOR = "#56d382";

export const getShiftColor = (shift: Shift, employee?: Employee): string => {
    if (!employee) {
        return shift.mandatory === true ? BRIS_DE_SERVICE : BESOIN_A_COMBLER;
    }

    const shiftStart = new Date(shift.start);
    const shiftEnd = new Date(shift.end);

    const shiftStartDateString = shiftStart.toISOString().slice(0, 10);
    const shiftEndDateString = shiftEnd.toISOString().slice(0, 10);

    const nextDayMidnight = new Date(Date.UTC(
        shiftStart.getUTCFullYear(),
        shiftStart.getUTCMonth(),
        shiftStart.getUTCDate() + 1,
        0, 0, 0, 0
    ));

    const isAfterNextDayMidnight = shiftEnd.getTime() > nextDayMidnight.getTime();

    const matches = (dates: string[] | undefined) => {
        return dates?.includes(shiftStartDateString) ||
            (isAfterNextDayMidnight && dates?.includes(shiftEndDateString));
    };

    if (matches(employee.unavailableTimeSlots)) return UNAVAILABLE_COLOR;
    if (matches(employee.undesiredTimeSlots)) return UNDESIRED_COLOR;
    if (matches(employee.desiredTimeSlots)) return DESIRED_COLOR;

    if (shift.shiftId) {
        return OFFICIAL_SHIFT_COLOR;
    }
    if (shift.pinned) {
        return PINNED_SHIFT_COLOR;
    }


    return DEFAULT_COLOR;
};

export type ShiftAssignmentGroupKey = "available" | "working" | "undesired" | "leave" | "unqualified";
export type ShiftAssignmentWarningKey = Exclude<ShiftAssignmentGroupKey, "available">;

export type ShiftAssignmentCandidate = {
    id: string;
    name: string;
    warnings: ShiftAssignmentWarningKey[];
};

export type ShiftAssignmentGroup = {
    key: ShiftAssignmentGroupKey;
    items: ShiftAssignmentCandidate[];
};

export const SHIFT_ASSIGNMENT_GROUP_LABEL: Record<ShiftAssignmentGroupKey, string> = {
    available: "Disponibles",
    working: "Déjà assignés",
    undesired: "Indisponibles",
    leave: "En congé",
    unqualified: "Non-qualifiés pour le rôle",
};

export const SHIFT_ASSIGNMENT_WARNING_UI: Record<ShiftAssignmentWarningKey, { label: string; color: string }> = {
    working: { label: "déjà assigné", color: DEFAULT_COLOR },
    undesired: { label: "indisponible", color: AVAILABILITY_CONFIG.undesired.bg },
    leave: { label: "en congé", color: AVAILABILITY_CONFIG.unavailable.bg },
    unqualified: { label: "non qualifié", color: UNQUALIFIED_COLOR },
};

const rangesOverlap = (aStart: number, aEnd: number, bStart: number, bEnd: number): boolean => {
    if (Number.isNaN(aStart) || Number.isNaN(aEnd) || Number.isNaN(bStart) || Number.isNaN(bEnd)) {
        return false;
    }

    return aStart < bEnd && bStart < aEnd;
};

const timeSlotsOverlap = (
    slots: TimeSlots[] | undefined,
    startMs: number,
    endMs: number,
): boolean => {
    if (!slots) {
        return false;
    }

    return slots.some(slot => {
        const slotStart = new Date(slot.start).getTime();
        const slotEnd = new Date(slot.end).getTime();

        return rangesOverlap(slotStart, slotEnd, startMs, endMs);
    });
};

export const categorizeAssignableEmployees = (
    shift: Shift,
    schedule: Schedule,
): ShiftAssignmentGroup[] => {
    const startMs = new Date(shift.start).getTime();
    const endMs = new Date(shift.end).getTime();
    const roleName = shift.roleName || shift.requiredSkill || "";

    const buckets: Record<ShiftAssignmentGroupKey, { candidate: ShiftAssignmentCandidate; sortKey: string }[]> = {
        available: [],
        working: [],
        undesired: [],
        leave: [],
        unqualified: [],
    };

    for (const employee of schedule.employees) {
        if (!employee.id) {
            continue;
        }

        const hasRole = employee.roles?.some(role => {
            const matchesById = !!shift.roleId && role.id === shift.roleId;
            const matchesByName = !!roleName && role.name === roleName;

            return matchesById || matchesByName;
        });

        const onLeave = timeSlotsOverlap(employee.unavailableTimeSlots, startMs, endMs);
        const undesired = timeSlotsOverlap(employee.undesiredTimeSlots, startMs, endMs);

        const working = schedule.shifts.some(other => {
            if (other.id === shift.id) {
                return false;
            }

            if (other.employee?.id !== employee.id) {
                return false;
            }

            const otherStart = new Date(other.start).getTime();
            const otherEnd = new Date(other.end).getTime();

            return rangesOverlap(otherStart, otherEnd, startMs, endMs);
        });

        const warnings: ShiftAssignmentWarningKey[] = [];

        if (!hasRole) {
            warnings.push("unqualified");
        }

        if (working) {
            warnings.push("working");
        }

        if (undesired) {
            warnings.push("undesired");
        }

        if (onLeave) {
            warnings.push("leave");
        }

        // Missing the role outranks every other state: the employee cannot take the shift at all.
        let groupKey: ShiftAssignmentGroupKey = "available";

        if (!hasRole) {
            groupKey = "unqualified";
        } else if (onLeave) {
            groupKey = "leave";
        } else if (undesired) {
            groupKey = "undesired";
        } else if (working) {
            groupKey = "working";
        }

        buckets[groupKey].push({
            candidate: { id: employee.id, name: employee.name, warnings },
            sortKey: getLastNameSortKey(employee),
        });
    }

    const order: ShiftAssignmentGroupKey[] = ["available", "working", "undesired", "leave", "unqualified"];
    return order.map(key => ({
        key,
        items: buckets[key]
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey, "fr", { sensitivity: "base" }))
            .map(entry => entry.candidate),
    }));
};

export const getShiftDisplayContent = (shift: Shift): string => {
    if (shift.employee) {
        return formatShortName(shift.employee.name);
    }
    return shift.mandatory === true ? "Bris de service" : "À combler";
};

export const isShiftVisibleForFilter = (
    shift: Shift,
    mode: "byLocation" | "byEmployee",
    selectedRoles: Set<string>,
    selectedEmployees: Set<string>,
): boolean => mode === "byEmployee"
    ? !!shift.employee && selectedEmployees.has(shift.employee.userId)
    : selectedRoles.has(shift.roleName || shift.requiredSkill || "Autre");

export const isShiftVisibleInCalendar = (
    shift: Shift,
    selectedRoles: Set<string>,
    selectedEmployees: Set<string>,
): boolean => {
    const role = shift.roleName || shift.requiredSkill || "Autre";

    if (!selectedRoles.has(role)) {
        return false;
    }

    if (!shift.employee) {
        return true;
    }

    return selectedEmployees.has(shift.employee.userId);
};

export const getStartOfWeek = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
};

export const getWeeksBetweenDates = (startDate: Date, endDate: Date): Date[] => {
    const weeks: Date[] = [];
    let current = getStartOfWeek(new Date(startDate));
    const end = new Date(endDate);

    while (current <= end) {
        weeks.push(new Date(current));
        current.setDate(current.getDate() + 7);
    }

    return weeks;
};

export const formatShiftTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export const formatDayLabel = (date: Date): string => {
    const days = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
    const months = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
    const dayName = days[date.getDay()];
    const dayNum = date.getDate().toString().padStart(2, "0");
    const monthName = months[date.getMonth()];
    return `${dayName} ${dayNum} ${monthName}`;
};

export const getShiftTooltipData = (
    shiftId: string,
    schedule: Schedule,
    shiftConstraintBreaches?: { [shiftId: string]: string[] }
): {
    lines: string[];
    timeRange: string;
    employeeName: string;
    roleName: string;
} | null => {
    const lines = shiftConstraintBreaches?.[shiftId] || [];
    const shift = schedule.shifts.find(s => s.id === shiftId);

    // if(lines.length>0){
    //     console.log("line");
    // }
    if (!shift) {
        return null;
    }

    const timeRange = `${formatShiftTime(shift.start)} - ${formatShiftTime(shift.end)}`;
    const employeeName = shift.employee?.name || "Non assigné";
    const roleName = shift.roleName || shift.requiredSkill || "";

    return {
        lines,
        timeRange,
        employeeName,
        roleName
    };
};

export const getTooltipPosition = (
    clientX: number,
    clientY: number,
    containerRect: DOMRect
): { x: number; y: number } => {
    const x = clientX - containerRect.left;
    const y = clientY - containerRect.top;
    return { x, y };
};
